import { createPortal } from "react-dom";
import { motion } from "framer-motion";
import { ReactNode, ComponentProps, CSSProperties, useEffect } from "react";

interface AnimationWrapperForPopupProps {
    children: ReactNode;
    noBackdrop?: boolean;
    direction?: "horizontal" | "vertical";
    isPhonePopup?: boolean;
    onClose?: () => void;
    noAnimation?: boolean;
}

interface AnimationWrapperForPopupPortalProps extends AnimationWrapperForPopupProps {}

type MotionDivProps = ComponentProps<typeof motion.div>;

const AnimationWrapperForPopup: React.FC<AnimationWrapperForPopupProps> = ({ children, noBackdrop, direction, isPhonePopup, onClose, noAnimation }) => {
    useEffect(() => {
        // Disable scrolling on mount
        document.body.style.overflow = 'hidden';
        
        // Re-enable scrolling on unmount
        return () => {
            document.body.style.overflow = '';
        };
    }, []);

    const initValue = () => {
        if (isPhonePopup) {
            return { opacity: 0 };
        }
        if (direction === "horizontal") {
            return { x: window.innerWidth };
        } else {
            return { y: window.innerHeight };
        }
    };

    const endValue = () => {
        if (isPhonePopup) {
            return { opacity: 1 };
        }
        if (direction === "horizontal") {
            return { x: 0 };
        } else {
            return { y: 0 };
        }
    };

    const popupAnimation: Omit<MotionDivProps, "style"> & { style: CSSProperties } = {
        initial: noAnimation ? endValue() : initValue(),
        animate: endValue(),
        exit: noAnimation ? endValue() : initValue(),
        transition: {
            duration: noAnimation ? 0 : (isPhonePopup ? 0.2 : 0.5),
            ease: isPhonePopup ? "easeOut" : [0.32, 0.72, 0, 1]
        },
        style: { position: "fixed", zIndex: 102, width: "100%", height: "100%", bottom: 0, left: 0 },
    };

    const backdropAnimation = {
        initial: noAnimation ? { opacity: 1 } : { opacity: 0 },
        animate: { opacity: 1 },
        exit: noAnimation ? { opacity: 1 } : { opacity: 0 },
        transition: {
            duration: noAnimation ? 0 : 0.4,
            ease: [0.4, 0, 0.2, 1]
        }
    };

    return (
        <div>
            {noBackdrop ? null : (
                <motion.div 
                    {...backdropAnimation}
                    onClick={onClose}
                    style={{ 
                        height: "100%", 
                        width: "100%", 
                        position: "fixed", 
                        left: 0, 
                        bottom: 0, 
                        zIndex: 101, 
                        background: "rgba(0, 0, 0, 0.3)", 
                        backdropFilter: "blur(0px)" 
                    }} 
                />
            )}
            <motion.div {...popupAnimation} key="popupAnimation">
                {children}
            </motion.div>
        </div>
    );
};

const AnimationWrapperForPopupPortal: React.FC<AnimationWrapperForPopupPortalProps> = ({ children, noBackdrop, direction, isPhonePopup, onClose, noAnimation }) => {
    return createPortal(
        <AnimationWrapperForPopup noBackdrop={noBackdrop} direction={direction} isPhonePopup={isPhonePopup} onClose={onClose} noAnimation={noAnimation}>
            {children}
        </AnimationWrapperForPopup>,
        document.getElementById("root") as HTMLElement
    );
};

export default AnimationWrapperForPopupPortal;
